import { AfterViewInit, ChangeDetectionStrategy, Component, effect, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { iframely } from '@iframely/embed.js';
import { ExternalContentSummary } from '../../../models';
import { CookieConsentService } from '../../../services/cookie-consent/cookie-consent.service';

@Component({
  selector: 'ptl-external-preview',
  templateUrl: './external-preview.component.html',
  styleUrls: ['./external-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalPreviewComponent implements AfterViewInit {
  @Input() content: ExternalContentSummary;
  @Input() folioAttachmentPreviewView = false;

  @ViewChild('htmlContent', { static: true }) public htmlContent: ElementRef;

  readonly embedsAllowed = inject(CookieConsentService).embeddedContentAllowed;

  private viewReady = false;
  private mountedNodes: ChildNode[] = [];

  constructor() {
    effect(() => {
      const allowed = this.embedsAllowed();
      if (!this.viewReady) {
        return;
      }
      if (allowed) {
        this.mountEmbed();
      } else {
        this.unmountEmbed();
      }
    });
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.mountEmbed();
  }

  openUrl(): void {
    if (this.content?.url) {
      window.open(this.content.url, '_self');
    }
  }

  private mountEmbed(): void {
    if (!this.content?.html || !this.embedsAllowed() || this.mountedNodes.length > 0) {
      return;
    }
    const fragment = document.createRange().createContextualFragment(this.wrapHtmlInStyleContainer(this.content.html));

    const iframeTitle = this.content.title || this.content.siteName || this.content.url || 'Embedded content';
    fragment.querySelectorAll('iframe').forEach((iframe) => {
      if (!iframe.hasAttribute('referrerpolicy')) {
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      }
      if (!iframe.getAttribute('title')) {
        iframe.setAttribute('title', iframeTitle);
      }
    });

    this.mountedNodes = Array.from(fragment.childNodes);
    this.htmlContent.nativeElement.prepend(fragment);
    setTimeout(() => {
      iframely.load();
      (this.htmlContent.nativeElement as HTMLElement).querySelectorAll('iframe').forEach((iframe) => {
        if (!iframe.getAttribute('title')) {
          iframe.setAttribute('title', iframeTitle);
        }
      });
    }, 500);
  }

  private unmountEmbed(): void {
    this.mountedNodes.forEach((node) => node.parentNode?.removeChild(node));
    this.mountedNodes = [];
  }

  private wrapHtmlInStyleContainer(html: string): string {
    const htmlWrapperOpening = '<div class="content-media__html">';
    const htmlWrapperClosing = '</div>';
    if (html.indexOf('iframely-embed') === -1) {
      return htmlWrapperOpening + html + htmlWrapperClosing;
    }

    return html;
  }
}
